import { useMutation, gql } from "@apollo/client";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React, { useContext, useEffect, useState } from "react";
import OtpInput from "react-otp-input";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { reactivateAccount, requestAccountReactivation } from "../../apollo/server";
import LogoIcon from "../../assets/images/logo.png";
import FlashMessage from "../../components/FlashMessage";
import UserContext from "../../context/User";
import useStyles from "../VerifyForgotOtp/styles";
import { LoginWrapper } from "../Wrapper";

const REQUEST = gql`
  ${requestAccountReactivation}
`;
const REACTIVATE = gql`
  ${reactivateAccount}
`;
const RESEND_SECONDS = 30;

// A customer signing in to an account they deleted: they confirm they want it
// back, a code goes to the account's email, and entering it reactivates the
// account. Setting the token signs them in, and AuthRoute takes them onward.
function Reactivate() {
  const theme = useTheme();
  const classes = useStyles();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { setTokenAsync } = useContext(UserContext);
  const [step, setStep] = useState("confirm");
  const [code, setCode] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [sendCode, { loading: sending }] = useMutation(REQUEST);
  const [reactivate, { loading: confirming }] = useMutation(REACTIVATE);
  const email = state?.email;

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const timer = setTimeout(() => setSeconds(seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);

  if (!email) return <Navigate replace to="/login" />;

  const send = async () => {
    setError("");
    try {
      await sendCode({ variables: { email } });
      setStep("code");
      setCode("");
      setSeconds(RESEND_SECONDS);
    } catch (e) {
      setError(e.message);
    }
  };

  const submit = async (value) => {
    if (value.length !== 6 || confirming) return;
    setError("");
    try {
      const { data } = await reactivate({ variables: { email, code: value } });
      await setTokenAsync(data.reactivateAccount.token);
    } catch (e) {
      // A new key puts the cursor back in the first box for the retry.
      setCode("");
      setAttempt((n) => n + 1);
      setError(e.message);
    }
  };

  const handleCode = (value) => {
    setCode(value);
    if (error) setError("");
    if (value.length === 6) submit(value);
  };

  return (
    <LoginWrapper>
      <FlashMessage
        open={Boolean(error) && step === "confirm"}
        severity="error"
        alertMessage={error}
      />
      <Box display="flex">
        <Box m="auto" sx={{ width: 90, height: 64, overflow: "hidden" }}>
          <img
            alt="RushMyFood"
            src={LogoIcon}
            style={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
              objectPosition: "top",
              transform: "scale(1.55)",
              transformOrigin: "top center",
            }}
          />
        </Box>
      </Box>

      {step === "confirm" ? (
        <>
          <Typography variant="h5" className={classes.font700}>
            Welcome back
          </Typography>
          <Box mt={theme.spacing(1)} />
          <Typography
            variant="caption"
            className={`${classes.caption} ${classes.fontGrey}`}
            style={{ overflowWrap: "anywhere" }}
          >
            The account for <strong>{email}</strong> was deleted. Would you like
            to reactivate it? We'll email you a 6-digit code to confirm it's you.
          </Typography>
          <Box mt={theme.spacing(4)} />
          <Button
            variant="contained"
            fullWidth
            disableElevation
            disabled={sending}
            className={classes.btnBase}
            onClick={send}
          >
            {sending ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <Typography
                variant="caption"
                className={`${classes.caption} ${classes.font700}`}
              >
                Yes, reactivate my account
              </Typography>
            )}
          </Button>
          <Box mt={theme.spacing(1)} />
          <Button
            onClick={() => navigate(-1)}
            style={{ textTransform: "none" }}
          >
            <Typography variant="caption" className={classes.font700}>
              Not now
            </Typography>
          </Button>
        </>
      ) : (
        <>
          <Typography variant="h5" className={classes.font700}>
            Enter your code
          </Typography>
          <Box mt={theme.spacing(1)} />
          <Typography
            variant="caption"
            className={`${classes.caption} ${classes.fontGrey}`}
            style={{ overflowWrap: "anywhere" }}
          >
            We sent a 6-digit code to <strong>{email}</strong>.
          </Typography>
          <Box mt={theme.spacing(3)} />
          <Box display="flex">
            <Box m="auto">
              <OtpInput
                key={attempt}
                shouldAutoFocus
                value={code}
                onChange={handleCode}
                numInputs={6}
                containerStyle={{
                  width: "100%",
                  display: "flex",
                  alignSelf: "center",
                }}
                inputStyle={{
                  width: 45,
                  height: 45,
                  margin: 5,
                  borderRadius: 5,
                  fontSize: theme.typography.h2,
                  border: "none",
                  boxShadow: theme.shadows[3],
                }}
                focusStyle={{ outlineColor: theme.palette.grey[900] }}
                renderInput={(props) => <input {...props} />}
              />
            </Box>
          </Box>
          {error ? (
            <Typography style={{ color: "red", fontSize: 14, marginTop: 12 }}>
              {error}
            </Typography>
          ) : null}
          <Box mt={theme.spacing(4)} />
          <Button
            variant="contained"
            fullWidth
            disableElevation
            disabled={confirming || code.length !== 6}
            className={classes.btnBase}
            onClick={() => submit(code)}
          >
            {confirming ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <Typography
                variant="caption"
                className={`${classes.caption} ${classes.font700}`}
              >
                Reactivate my account
              </Typography>
            )}
          </Button>
          <Box mt={theme.spacing(1)} />
          <Button
            disabled={seconds > 0 || sending}
            onClick={send}
            style={{ textTransform: "none" }}
          >
            <Typography variant="caption" className={classes.font700}>
              {seconds > 0 ? `Resend code in ${seconds}s` : "Resend code"}
            </Typography>
          </Button>
        </>
      )}
    </LoginWrapper>
  );
}

export default Reactivate;
